/**
 * SuggestedCareManagersTable: the one table the intake card, the Assign
 * care manager dialog and the map's "Place a client" render for a ranked
 * list of care managers. Two blocks, Available (Open and Talk to Me,
 * together) then Not available (Not Open and On leave), each in the order
 * given (nearest first). Each row reads the name, then its stance chip
 * (Open, Talk to Me or Not Open in the stance's colour, or On leave in
 * its grey), then the unit on its own line under the name. Distance and last week's
 * hours are columns, named once in the header. Every row is drawn at full
 * strength and stays selectable: the block heading and the chip say why
 * a row is not available. Nothing here is computed from hours.
 *
 * The stance on each row is the one the server sent. A block gets its
 * heading only when every row in it carries a stance, and a row gets its
 * chip and data-available only when it carries one, so rows sent without
 * a stance read as one plain list.
 *
 * When the columns do not fit, each row becomes a block: the name, chip
 * and unit, then Distance and Last week on labelled lines. A container
 * query on the table's wrapper makes the switch. Its breakpoint is in
 * rem, so larger text switches at a wider panel, and it is the table's
 * widest measured min-content rounded up to the next half rem: 15.5rem,
 * or 20rem when any row renders an action, since the map's Assign button
 * widens every row.
 *
 * Selecting a row is the caller's action (fill a picker, draw a ray);
 * an optional per-row action cell (the map's Assign button) sits beside
 * it, never inside it, since a <button> cannot nest another. The action
 * column shows only when some row's action has content.
 */

import { Fragment, type ReactNode } from 'react';
import { STATED_AVAILABILITY_CLASSES, STATED_AVAILABILITY_LABEL, formatHours, isAvailableStance, type StatedAvailability } from '@/lib/capacity';
import { StanceChip } from './StanceChip';

export interface SuggestedCareManagerRow {
  roster_id: string;
  full_name: string;
  unit_id: string | null;
  unit_name: string | null;
  /** Absent when the server did not send it (a seat that does not
   *  assign): the cell reads "Not shown". */
  distance_miles?: number;
  /** The stance the server sent; absent when it sent none. */
  stated_availability?: StatedAvailability;
  /** Billable hours in the previous complete week, numeric text. */
  hours_worked_last_week: string;
}

export interface SuggestedCareManagersTableProps {
  available: readonly SuggestedCareManagerRow[];
  unavailable: readonly SuggestedCareManagerRow[];
  selectedId: string | null;
  onSelect: (rosterId: string) => void;
  /** The table's own testid; row and cell testids hang off `testIdPrefix`. */
  listTestId: string;
  testIdPrefix: string;
  /**
   * An action cell per row (the map's Assign button). The column renders
   * only when at least one row's action has content; a row with none gets
   * an empty cell.
   */
  renderAction?: (row: SuggestedCareManagerRow) => ReactNode;
}

/**
 * The block layout's classes at each breakpoint, written out whole so
 * Tailwind finds every class. `wide` is for a table with an action column.
 */
const BLOCK_LAYOUT = {
  narrow: {
    table: '@max-[15.5rem]:block',
    head: '@max-[15.5rem]:hidden',
    body: '@max-[15.5rem]:block',
    headingRow: '@max-[15.5rem]:block',
    headingCell: '@max-[15.5rem]:block',
    row: '@max-[15.5rem]:mb-1 @max-[15.5rem]:block @max-[15.5rem]:rounded-xl @max-[15.5rem]:px-3 @max-[15.5rem]:py-2',
    nameCell: '@max-[15.5rem]:block @max-[15.5rem]:rounded-none @max-[15.5rem]:p-0',
    factCell:
      '@max-[15.5rem]:mt-0.5 @max-[15.5rem]:flex @max-[15.5rem]:flex-wrap @max-[15.5rem]:items-baseline @max-[15.5rem]:gap-x-2 @max-[15.5rem]:rounded-none @max-[15.5rem]:p-0 @max-[15.5rem]:text-left',
    actionCell: '@max-[15.5rem]:mt-1.5 @max-[15.5rem]:block @max-[15.5rem]:rounded-none @max-[15.5rem]:p-0 @max-[15.5rem]:text-left',
    label: '@max-[15.5rem]:inline',
  },
  wide: {
    table: '@max-[20rem]:block',
    head: '@max-[20rem]:hidden',
    body: '@max-[20rem]:block',
    headingRow: '@max-[20rem]:block',
    headingCell: '@max-[20rem]:block',
    row: '@max-[20rem]:mb-1 @max-[20rem]:block @max-[20rem]:rounded-xl @max-[20rem]:px-3 @max-[20rem]:py-2',
    nameCell: '@max-[20rem]:block @max-[20rem]:rounded-none @max-[20rem]:p-0',
    factCell:
      '@max-[20rem]:mt-0.5 @max-[20rem]:flex @max-[20rem]:flex-wrap @max-[20rem]:items-baseline @max-[20rem]:gap-x-2 @max-[20rem]:rounded-none @max-[20rem]:p-0 @max-[20rem]:text-left',
    actionCell: '@max-[20rem]:mt-1.5 @max-[20rem]:block @max-[20rem]:rounded-none @max-[20rem]:p-0 @max-[20rem]:text-left',
    label: '@max-[20rem]:inline',
  },
} as const;

/** A fact's label: hidden while the header names the column, shown in the block layout. */
const FACT_LABEL = 'hidden text-label tracking-label font-semibold uppercase text-sage-400';

/** Whether an action renders anything at all. */
function hasContent(node: ReactNode): boolean {
  return node !== null && node !== undefined && node !== false && node !== true && node !== '';
}

export function SuggestedCareManagersTable({
  available,
  unavailable,
  selectedId,
  onSelect,
  listTestId,
  testIdPrefix,
  renderAction,
}: SuggestedCareManagersTableProps): ReactNode {
  const actions = new Map<string, ReactNode>(
    [...available, ...unavailable].map((row) => [row.roster_id, renderAction ? renderAction(row) : null]),
  );
  const hasActions = Array.from(actions.values()).some(hasContent);
  const layout = hasActions ? BLOCK_LAYOUT.wide : BLOCK_LAYOUT.narrow;
  const columns = hasActions ? 4 : 3;
  const blocks = [
    ['available', 'Available', available],
    ['not-available', 'Not available', unavailable],
  ] as const;
  return (
    <div className="@container">
      <table className={`w-full text-sm border-separate border-spacing-y-1 ${layout.table}`} data-testid={listTestId}>
        <thead className={layout.head}>
          <tr className="text-left text-label uppercase tracking-[0.12em] text-sage-400">
            <th scope="col" className="pl-3 pr-2 py-1 font-semibold">Care manager</th>
            <th scope="col" className="px-2 py-1 font-semibold text-right">Distance</th>
            <th scope="col" className="px-2 py-1 font-semibold text-right">Last week</th>
            {hasActions && <th scope="col" className="pl-2 pr-3 py-1"><span className="sr-only">Action</span></th>}
          </tr>
        </thead>
        <tbody className={layout.body}>
          {blocks.map(([block, heading, rows]) =>
            rows.length === 0 ? null : (
              <Fragment key={block}>
                {rows.every((row) => row.stated_availability !== undefined) && (
                  <tr className={layout.headingRow} data-testid={`${testIdPrefix}-group-${block}`}>
                    <td colSpan={columns} className={`pl-3 pt-1 text-xs font-semibold text-ink-soft ${layout.headingCell}`}>{heading}</td>
                  </tr>
                )}
                {rows.map((row) => {
                  const stance = row.stated_availability;
                  const isSelected = row.roster_id === selectedId;
                  return (
                    <tr
                      key={row.roster_id}
                      data-testid={`${testIdPrefix}-row-${row.roster_id}`}
                      data-available={stance === undefined ? undefined : String(isAvailableStance(stance))}
                      className={`${isSelected ? 'bg-sage-100' : 'bg-sand'} ${layout.row}`}
                    >
                      <td className={`pl-3 pr-2 py-2 rounded-l-xl ${layout.nameCell}`}>
                        <button
                          type="button"
                          onClick={() => onSelect(row.roster_id)}
                          aria-pressed={isSelected}
                          data-testid={`${testIdPrefix}-select-${row.roster_id}`}
                          className="min-w-0 w-full rounded-base text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                        >
                          <span className="text-sm font-semibold text-sage-800">{row.full_name}</span>
                          {stance !== undefined &&
                            (stance === 'on_leave' ? (
                              <span
                                className={`ml-1.5 inline-flex rounded-pill px-1.5 py-0.5 text-label font-semibold align-middle ${STATED_AVAILABILITY_CLASSES.on_leave}`}
                                data-testid={`${testIdPrefix}-stance-${row.roster_id}`}
                                data-stance={stance}
                              >
                                {STATED_AVAILABILITY_LABEL[stance]}
                              </span>
                            ) : (
                              <StanceChip
                                stance={stance}
                                className="ml-1.5 align-middle"
                                data-testid={`${testIdPrefix}-stance-${row.roster_id}`}
                              />
                            ))}
                          {row.unit_name && (
                            <span className="block text-xs text-ink-soft" data-testid={`${testIdPrefix}-unit-${row.roster_id}`}>
                              {row.unit_name}
                            </span>
                          )}
                        </button>
                      </td>
                      <td
                        className={`px-2 py-2 text-right tabular-nums text-xs text-ink-soft whitespace-nowrap ${layout.factCell}`}
                        data-testid={`${testIdPrefix}-distance`}
                      >
                        <span className={`${FACT_LABEL} ${layout.label}`}>Distance</span>
                        {row.distance_miles === undefined ? 'Not shown' : `${row.distance_miles.toFixed(1)} mi`}
                      </td>
                      <td
                        className={`px-2 py-2 text-right tabular-nums text-xs text-ink-soft whitespace-nowrap${hasActions ? '' : ' rounded-r-xl'} ${layout.factCell}`}
                        data-testid={`${testIdPrefix}-last-week`}
                      >
                        <span className={`${FACT_LABEL} ${layout.label}`}>Last week</span>
                        {formatHours(row.hours_worked_last_week) || '0'} h
                      </td>
                      {hasActions && (
                        <td className={`pl-2 pr-3 py-2 text-right rounded-r-xl whitespace-nowrap ${layout.actionCell}`}>
                          {actions.get(row.roster_id)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </Fragment>
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}
